import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { TenantRole } from '@kioskforge/shared';
import { NavigationRail } from '../components/ui';
import { applyTheme } from '../theme/applyTheme';
import { navigation } from './navigation';
import { useSession } from './session';
import { ClearToastsOnNavigate } from './ToastProvider';

const THEME_KEY = 'kioskforge-theme';

function ThemeSwitcher() {
  const [theme, setTheme] = useState(document.documentElement.dataset.theme ?? 'signal');

  async function change(name: string) {
    setTheme(name);
    localStorage.setItem(THEME_KEY, name);
    const response = await fetch(`/themes/${name}.json`);
    if (!response.ok) return;
    applyTheme(await response.json());
  }

  return (
    <label className="theme-control">
      <span className="index-label">THEME</span>
      <select value={theme} onChange={(event) => void change(event.target.value)} aria-label="Interface theme">
        <option value="signal">Signal / Light</option>
        <option value="console">Console / Dark</option>
      </select>
    </label>
  );
}

function pageId(pathname: string): string {
  const match = navigation.find((item) => item.path !== '/' && pathname.startsWith(item.path));
  return match?.id ?? 'overview';
}

export function AppShell() {
  const { session, signOut } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  ClearToastsOnNavigate();

  const active = pageId(location.pathname);
  const role: TenantRole = session?.role ?? 'viewer';
  const tenantName = session?.tenant.name ?? 'KioskForge';

  return (
    <div className="app-shell">
      <aside className="side-shell">
        <a className="wordmark" href="/">
          KF<span>/</span>
          <b>CONTROL</b>
        </a>
        <button className="rail-toggle" type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'COLLAPSE' : 'EXPAND'}
        </button>
        <NavigationRail
          expanded={expanded}
          items={navigation}
          active={active}
          onSelect={(id) => {
            const target = navigation.find((item) => item.id === id);
            if (target) navigate(target.path);
          }}
        />
        <div className="rail-footer">
          <span className="status-pip">
            <i />
            {role.toUpperCase()}
          </span>
          <span className="mono">R1</span>
        </div>
      </aside>

      <div className="main-shell">
        <header className="topbar">
          <span className="index-label">
            {tenantName.toUpperCase()} / {active.toUpperCase()}
          </span>
          <div className="theme-control">
            <ThemeSwitcher />
            <button className="button button-quiet" type="button" onClick={signOut}>
              Sign out
            </button>
          </div>
        </header>
        <Outlet />
      </div>

      <nav className="bottom-bar" aria-label="Mobile navigation">
        {navigation.slice(0, 4).map(({ id, label, short }) => (
          <button
            key={id}
            type="button"
            className={active === id ? 'active' : ''}
            onClick={() => navigate(navigation.find((item) => item.id === id)?.path ?? '/')}
          >
            <span>{short}</span>
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
}
